import React, { useEffect, useState } from "react";
import HubTopBar from "../components/aa/HubTopBar";
import Footer from "../components/aa/Footer";
import { Button } from "../components/ui/button";
import { Check, Sparkles, BadgeCheck, ArrowRight, MessageCircle, TrendingUp, Wallet } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { PRICING } from "../data/pricing";
import { WHATSAPP_URL } from "../config";

export default function PricingPage() {
  const { lang } = useLang();
  const t = PRICING[lang] || PRICING.en;
  const [yearly, setYearly] = useState(false);
  const openWa = () => window.open(WHATSAPP_URL, "_blank", "noopener,noreferrer");

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    document.querySelectorAll(".reveal").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [lang]);

  const Cell = ({ v }) => {
    if (v === "y") return <Check className="w-4 h-4 mx-auto text-[var(--aa-wine)]" strokeWidth={2.5} />;
    if (v === "-") return <span className="text-[var(--aa-ink-soft)]/40">—</span>;
    return <span className="text-[13px] text-[var(--aa-ink-soft)]">{v}</span>;
  };

  return (
    <div className="min-h-screen bg-[var(--aa-cream)] text-[var(--aa-ink)] font-body">
      <HubTopBar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[var(--aa-wine)] text-[var(--aa-cream)]">
        <div className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full border border-white/10"></div>
        <div className="pointer-events-none absolute -bottom-56 -left-40 w-[640px] h-[640px] rounded-full border border-white/5"></div>
        <div className="relative mx-auto max-w-6xl px-6 md:px-8 pt-20 md:pt-28 pb-16 md:pb-24 text-center">
          <div className="reveal in inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/25 bg-white/5 text-[11px] tracking-[0.22em] uppercase">
            <Sparkles className="w-3.5 h-3.5" />
            {t.hero.eyebrow}
          </div>
          <h1 className="reveal font-display font-light text-[42px] leading-[1.05] sm:text-6xl md:text-[72px] md:leading-[1.02] mt-6 max-w-4xl mx-auto">
            {t.hero.h1a}
            <br />
            <span className="italic font-normal text-[var(--aa-cream)]/95">{t.hero.h1b}</span>
          </h1>
          <p className="reveal max-w-xl mx-auto mt-6 text-[15px] md:text-[17px] text-[var(--aa-cream)]/80">
            {t.hero.p}
          </p>
          <div className="reveal mt-8 inline-flex items-center gap-3 pl-1.5 pr-6 py-1.5 rounded-full bg-[var(--aa-cream)] text-[var(--aa-wine)] shadow-[0_10px_40px_-15px_rgba(0,0,0,0.5)]">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[var(--aa-wine)] text-[var(--aa-cream)]">
              <BadgeCheck className="w-4.5 h-4.5" strokeWidth={2.2} />
            </span>
            <span className="font-display italic text-xl md:text-2xl leading-none whitespace-nowrap">
              {t.hero.zero}{" "}
              <span className="text-[var(--aa-wine)]/55">
                {t.hero.ever}
                <sup className="text-[11px] ml-0.5 not-italic">
                  <a href="#fineprint">*</a>
                </sup>
              </span>
            </span>
          </div>
        </div>
      </section>

      {/* TIERS */}
      <section className="relative pt-14 pb-24 md:py-24 bg-[var(--aa-cream)]">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          {/* Toggle */}
          <div className="reveal flex items-center justify-center gap-3">
            <span className={`text-[13px] font-medium ${!yearly ? "text-[var(--aa-wine)]" : "text-[var(--aa-ink-soft)]"}`}>
              {t.toggle.monthly}
            </span>
            <button
              onClick={() => setYearly((v) => !v)}
              className={`relative inline-flex w-12 h-7 rounded-full transition-colors ${
                yearly ? "bg-[var(--aa-wine)]" : "bg-[var(--aa-wine)]/25"
              }`}
              aria-label="Toggle billing period"
            >
              <span
                className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                  yearly ? "translate-x-5" : "translate-x-0"
                }`}
              ></span>
            </button>
            <span className={`text-[13px] font-medium ${yearly ? "text-[var(--aa-wine)]" : "text-[var(--aa-ink-soft)]"}`}>
              {t.toggle.yearly}
            </span>
            <span className="ml-2 inline-flex items-center px-2.5 py-1 rounded-full bg-[var(--aa-wine)]/10 text-[var(--aa-wine)] text-[11px] font-semibold">
              {t.toggle.saveBadge}
            </span>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-5">
            {t.tiers.map((tier) => {
              const price = yearly ? tier.priceY : tier.priceM;
              const per = yearly ? tier.perY : tier.perM;
              const popular = tier.popular;
              return (
                <div
                  key={tier.name}
                  className={`reveal lift relative rounded-3xl p-7 md:p-8 border flex flex-col ${
                    popular
                      ? "bg-[var(--aa-wine)] text-[var(--aa-cream)] border-transparent shadow-[0_30px_80px_-30px_rgba(107,15,16,0.5)]"
                      : "bg-white border-[var(--aa-line)] text-[var(--aa-ink)]"
                  }`}
                >
                  {popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--aa-cream)] text-[var(--aa-wine)] text-[11px] font-semibold tracking-wide">
                      <Sparkles className="w-3 h-3" /> {t.popular}
                    </div>
                  )}
                  <div className={`text-[11px] uppercase tracking-[0.22em] font-semibold ${popular ? "text-[var(--aa-cream)]/70" : "text-[var(--aa-wine)]"}`}>
                    {tier.name}
                  </div>
                  <div className={`text-[13px] mt-2 leading-relaxed ${popular ? "text-[var(--aa-cream)]/80" : "text-[var(--aa-ink-soft)]"}`}>
                    {tier.forText}
                  </div>
                  <div className="mt-6 flex items-baseline gap-1">
                    <span className="font-display text-5xl md:text-[52px] leading-none">{price}</span>
                    {tier.name !== "Free" && (
                      <span className={`text-sm ${popular ? "text-[var(--aa-cream)]/70" : "text-[var(--aa-ink-soft)]"}`}>
                        /mo
                      </span>
                    )}
                  </div>
                  <div className={`text-xs mt-1 ${popular ? "text-[var(--aa-cream)]/60" : "text-[var(--aa-ink-soft)]"}`}>
                    {per}
                  </div>

                  <Button
                    onClick={openWa}
                    className={`w-full mt-6 rounded-full py-5 font-semibold text-[13.5px] group ${
                      popular
                        ? "bg-[var(--aa-cream)] hover:bg-white text-[var(--aa-wine)]"
                        : "bg-[var(--aa-wine)] hover:bg-[var(--aa-wine-deep)] text-[var(--aa-cream)]"
                    }`}
                  >
                    {tier.cta}
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                  </Button>

                  {tier.inheritKey && (
                    <div className={`mt-5 text-[11.5px] uppercase tracking-[0.18em] font-semibold ${popular ? "text-[var(--aa-cream)]/70" : "text-[var(--aa-wine)]"}`}>
                      {t.inherit[tier.inheritKey]}
                    </div>
                  )}

                  <ul className="mt-4 space-y-2.5">
                    {tier.features.map((f, i) => (
                      <li key={i} className={`flex items-start gap-2.5 text-[13.5px] ${f.note ? "pl-7 -mt-1.5 opacity-75" : ""}`}>
                        {!f.note && (
                          <span
                            className={`mt-0.5 inline-flex w-4.5 h-4.5 min-w-[18px] min-h-[18px] rounded-full items-center justify-center flex-shrink-0 ${
                              popular ? "bg-[var(--aa-cream)]/15 text-[var(--aa-cream)]" : "bg-[var(--aa-wine)]/10 text-[var(--aa-wine)]"
                            }`}
                          >
                            <Check className="w-3 h-3" strokeWidth={2.6} />
                          </span>
                        )}
                        <span className={popular ? "text-[var(--aa-cream)]/90" : "text-[var(--aa-ink)]"}>{f.t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MATH */}
      <section className="relative py-20 md:py-28 bg-[var(--aa-cream-warm)]">
        <div className="mx-auto max-w-5xl px-6 md:px-8">
          <div className="reveal max-w-3xl">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[var(--aa-wine)] font-semibold mb-4">
              {t.math.eyebrow}
            </div>
            <h2 className="font-display font-light text-4xl md:text-5xl leading-[1.05] text-[var(--aa-ink)]">
              {t.math.h2}
            </h2>
            <p className="mt-5 text-[15px] text-[var(--aa-ink-soft)] leading-relaxed">{t.math.p}</p>
          </div>

          <div className="reveal grid md:grid-cols-3 gap-4 mt-10">
            <div className="rounded-2xl p-6 border border-[var(--aa-line)] bg-white">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--aa-ink-soft)]">{t.math.lost.v}</div>
              <div className="font-display text-4xl md:text-5xl mt-3 text-[#8a1c1c] line-through decoration-[#8a1c1c]/40">
                {t.math.lost.k}
              </div>
            </div>
            <div className="rounded-2xl p-6 border border-[var(--aa-line)] bg-white">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--aa-ink-soft)]">{t.math.us.v}</div>
              <div className="font-display text-4xl md:text-5xl mt-3 text-[var(--aa-ink)] inline-flex items-center gap-2">
                <Wallet className="w-6 h-6 text-[var(--aa-wine)]" />
                {t.math.us.k}
              </div>
            </div>
            <div className="rounded-2xl p-6 border border-[var(--aa-wine)] bg-[var(--aa-wine)] text-[var(--aa-cream)] relative overflow-hidden">
              <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-white/10"></div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--aa-cream)]/80">{t.math.keep.v}</div>
              <div className="font-display text-4xl md:text-5xl mt-3 inline-flex items-center gap-2">
                <TrendingUp className="w-6 h-6" />
                {t.math.keep.k}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARE */}
      <section className="relative py-24 md:py-28 bg-[var(--aa-cream)]">
        <div className="mx-auto max-w-6xl px-4 md:px-8">
          <div className="reveal text-center mb-10">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[var(--aa-wine)] font-semibold mb-3">
              {t.compare.eyebrow}
            </div>
          </div>
          <div className="reveal overflow-x-auto rounded-3xl border border-[var(--aa-line)] bg-white">
            <table className="w-full text-left border-collapse min-w-[720px]">
              <thead>
                <tr className="border-b border-[var(--aa-line)]">
                  {t.compare.cols.map((c, i) => (
                    <th
                      key={i}
                      className={`px-5 py-4 text-[12px] uppercase tracking-[0.16em] font-semibold ${
                        i === 3 ? "bg-[var(--aa-cream-warm)] text-[var(--aa-wine)]" : "text-[var(--aa-ink)]"
                      } ${i === 0 ? "" : "text-center"}`}
                    >
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {t.compare.groups.map((g, gi) => (
                  <React.Fragment key={gi}>
                    <tr className="bg-[var(--aa-cream)]/60">
                      <td colSpan={5} className="px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-[var(--aa-wine)] font-semibold">
                        {g.name}
                      </td>
                    </tr>
                    {g.rows.map((r, ri) => (
                      <tr key={ri} className="border-t border-[var(--aa-line)]">
                        <td className="px-5 py-3 text-[13.5px] text-[var(--aa-ink)]">{r.label}</td>
                        {r.vals.map((v, vi) => (
                          <td
                            key={vi}
                            className={`px-5 py-3 text-center ${vi === 2 ? "bg-[var(--aa-cream-warm)]/50" : ""}`}
                          >
                            <Cell v={v} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative pb-8 md:pb-12 bg-[var(--aa-cream)]">
        <div className="mx-auto max-w-3xl px-6 md:px-8 text-center">
          <Button
            onClick={openWa}
            className="rounded-full bg-[var(--aa-wine)] hover:bg-[var(--aa-wine-deep)] text-[var(--aa-cream)] px-7 py-6 font-semibold"
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            Contact us on WhatsApp
          </Button>
        </div>
      </section>

      {/* FINE PRINT */}
      <section id="fineprint" className="relative pt-4 pb-24 md:pb-28 bg-[var(--aa-cream)]">
        <div className="mx-auto max-w-4xl px-6 md:px-8 space-y-3 text-[12px] text-[var(--aa-ink-soft)] leading-relaxed">
          {t.fine.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
